import { useApolloClient } from '@apollo/client';
import { Modal, notification } from 'antd';
import { isEmpty } from 'lodash/fp';
import { useEffect, useCallback, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import {
    ListenOnSystemDocument,
    type ListenOnSystemSubscription,
    type ListenOnSystemSubscriptionVariables,
} from '../api/subscriptions/listenOnSystem';
import { ALL_COMPANIES } from '../layouts/ConsoleLayout/ConsoleCompanyMenu';
import { useAccountContext } from './contexts/AccountContextManager';
import { useCompany } from './contexts/CompanyContextManager';

const SystemMessageHandler = () => {
    const { logout } = useAccountContext();
    const apolloClient = useApolloClient();
    const company = useCompany(true);

    const { t } = useTranslation(['common']);

    const messageHandler = useCallback(
        (message: ListenOnSystemSubscription['message']) => {
            switch (message.__typename) {
                // sign out if the session is revoked
                case 'UserSessionRevoked':
                    // logout
                    logout();

                    if (message.displayNotice) {
                        // and inform the user as requested
                        // we may skip the notice if the user logged out itself for example
                        Modal.warning({
                            title: t('common:sessionRevokeNotice.title'),
                            content: t('common:sessionRevokeNotice.content'),
                            okText: t('common:sessionRevokeNotice.okText'),
                        });
                    }
                    break;

                case 'MessageNotice':
                    notification.info({
                        message: t('common:systemNotice'),
                        description: message.message,
                        duration: 0,
                        placement: 'bottom',
                        key: 'MessageNotice',
                    });
                    break;

                case 'MaintenanceUpdate': {
                    // maintenance should be company specific
                    if (company?.id) {
                        PubSub.publish(`message.${company.id}.${message.__typename}`, message);
                    }
                    break;
                }

                default:
                    break;
            }
        },
        [logout, t, company?.id]
    );

    const messageHandlerRef = useRef(messageHandler);

    // update the reference
    // we use a reference so we don't trigger updates on the next useEffect
    // otherwise it would keep subscribing/unsubscribing for changes not related
    messageHandlerRef.current = messageHandler;

    useEffect(() => {
        const observer = apolloClient.subscribe<ListenOnSystemSubscription, ListenOnSystemSubscriptionVariables>({
            query: ListenOnSystemDocument,
            variables: { companyId: isEmpty(company?.id) || company.id === ALL_COMPANIES ? null : company.id },
        });

        const subscription = observer.subscribe(({ data }) => {
            if (messageHandlerRef.current) {
                messageHandlerRef.current(data.message);
            }
        });

        return () => subscription.unsubscribe();
    }, [apolloClient, messageHandlerRef, company?.id]);

    return null;
};

export default SystemMessageHandler;
